import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear';
import { addCmseCommitteeToSectionalCommitteeArray } from '../../utils/addCmseCommitteeToSectionalCommitteeArray';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import PDFDownloadButton from './NominationShortlist/PDFDownloadButton.jsx';
import CommitteeBanner from './CommitteeBanner';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  SE_COMMITTEE_ID,
} from '../../utils/constants';

const getNominationsForCommittee = (committee, nominations) =>
  nominations.filter((nomination) => {
    if (committee.id === CM_PRIMARY_ID) {
      return (
        nomination.attributes.electionTypeId ===
        ELECTION_TYPE.CORRESPONDING_MEMBER
      );
    }
    if (committee.id === SE_COMMITTEE_ID) {
      return (
        nomination.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
      );
    }
    console.log(nomination);
    return nomination.attributes.sectionalCommitteeId === committee.id;
  });

const SectionalCommittees = ({
  TableComponent,
  handleSelectClick,
  handleStage2SelectClick,
  handleCOIClick,
  statusTypes,
  selectedCommittee = null,
  selectedNominationStatus = null,
  selectedRankingFilter = null,
  showMyAllocations = false,
  onCandidateCountChange = null,
  onRankingSuccess = null,
  isSCChair = false,
  enableCouncilMembers = true,
  isCouncilBannerVisible = false,
}) => {
  const { userDetails } = useLoggedInUserContextProvider();
  const { sectionalCommitteesArray } = useStaticCollections();
  const {
    nominationArray,
    committeeArray,
    candidateArray,
    proposerSeconder,
    nominationStateArray,
  } = useAdminCollections();
  const location = useLocation();
  const [nominations, setNominations] = useState([]);
  const [sectionalCommitteeArray, setSectionalCommitteeArray] = useState([]);

  const {
    isAdmin,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    userCommittees,
  } = useUserPermissions(userDetails, committeeArray);

  const isCouncilMemberOnly = useMemo(() => {
    if (!enableCouncilMembers) {
      return false;
    }
    return isCouncilMember && !isAdmin;
  }, [isCouncilMember, enableCouncilMembers, isAdmin]);

  const allocatedNominationIds = useMemo(() => {
    if (!isCouncilMemberOnly) {
      return null;
    }

    if (!nominationStateArray || nominationStateArray.length === 0) {
      return [];
    }

    return nominationStateArray
      .filter((state) => {
        const councilMemberUserId =
          state.attributes?.councilMemberUserId || state.councilMemberUserId;
        return councilMemberUserId === userDetails.id;
      })
      .map((state) => state.attributes?.nominationId || state.nominationId);
  }, [isCouncilMemberOnly, nominationStateArray, userDetails?.id]);

  const isDisplayPDFDownloadButton =
    location.pathname.includes('elect-candidate') ||
    location.pathname.includes('assessor-administration');

  const filteredSectionalCommittees = sectionalCommitteeArray.filter(
    (sectionalCommittee) => {
      if (selectedCommittee === null) {
        return true;
      }

      const selectedCommitteeId = parseInt(selectedCommittee);

      return sectionalCommittee?.id === selectedCommitteeId;
    },
  );

  const currentStatus = selectedNominationStatus || statusTypes;

  const committeesWithNominations = useMemo(
    () =>
      filteredSectionalCommittees.map((committee) => {
        let nominationsInSectionalCommittee = getNominationsForCommittee(
          committee,
          nominations,
        );

        if (
          showMyAllocations &&
          isCouncilMemberOnly &&
          allocatedNominationIds
        ) {
          nominationsInSectionalCommittee =
            nominationsInSectionalCommittee.filter((nomination) =>
              allocatedNominationIds.includes(nomination.id),
            );
        }

        if (
          selectedNominationStatus &&
          Array.isArray(selectedNominationStatus)
        ) {
          nominationsInSectionalCommittee =
            nominationsInSectionalCommittee.filter((nomination) =>
              selectedNominationStatus.includes(
                nomination.attributes.nominationStatusTypeId,
              ),
            );
        }

        if (selectedRankingFilter) {
          nominationsInSectionalCommittee =
            nominationsInSectionalCommittee.filter((nomination) => {
              const nominationState = nominationStateArray?.find(
                (state) => state.attributes.nominationId === nomination.id,
              );

              const hasRanking = nominationState?.attributes?.ranking != null;
              const isRecommendedToCouncil =
                nomination.attributes.nominationStatusTypeId ===
                NOMINATION_STATUS.RECOMMENDED;

              if (selectedRankingFilter === 'ranked') {
                return hasRanking;
              }

              if (selectedRankingFilter === 'unranked') {
                return isRecommendedToCouncil && !hasRanking;
              }

              if (selectedRankingFilter === 'overlap') {
                return nomination.attributes.overlapCommittee;
              }

              return true;
            });
        }

        let overlapNominations = nominations
          .filter(
            (nomination) =>
              nomination.attributes.overlapSectionalCommitteeId ===
                committee.id &&
              nomination.attributes.sectionalCommitteeId !== committee.id &&
              nomination.attributes.electionTypeId === ELECTION_TYPE.ORDINARY &&
              committee.id !== CM_PRIMARY_ID,
          )
          .map((nomination) => ({
            ...nomination,
            isOverlapCommittee: true,
          }));

        if (selectedRankingFilter) {
          overlapNominations = overlapNominations.filter((nomination) => {
            const nominationState = nominationStateArray?.find(
              (state) => state.attributes.nominationId === nomination.id,
            );

            const hasRanking = nominationState?.attributes?.ranking != null;

            if (selectedRankingFilter === 'ranked') {
              return hasRanking && !nomination.attributes.overlapCommittee;
            }

            return true;
          });
        }

        if (selectedNominationStatus?.includes(NOMINATION_STATUS.RECOMMENDED)) {
          overlapNominations = [];
        }

        let allNominations;

        if (isAdmin || isSCSecretary || isCouncilMember) {
          allNominations = [...nominationsInSectionalCommittee].sort((a, b) =>
            a.attributes.candidate?.localeCompare(b.attributes.candidate),
          );
        } else {
          allNominations = [
            ...nominationsInSectionalCommittee,
            ...overlapNominations,
          ].sort((a, b) =>
            a.attributes.candidate?.localeCompare(b.attributes.candidate),
          );
        }

        const totalCount = allNominations?.length;

        return {
          committee,
          nominationsInSectionalCommittee: allNominations,
          totalNominationCount: totalCount,
          overlapNominations,
        };
      }),
    [
      filteredSectionalCommittees,
      nominations,
      selectedNominationStatus,
      selectedRankingFilter,
      nominationArray,
      proposerSeconder,
      statusTypes,
      nominationStateArray,
      showMyAllocations,
      isCouncilMemberOnly,
      allocatedNominationIds,
    ],
  );

  console.log(committeesWithNominations);

  useEffect(() => {
    if (onCandidateCountChange) {
      const totalCandidates = committeesWithNominations.reduce(
        (total, { nominationsInSectionalCommittee }) =>
          total + nominationsInSectionalCommittee.length,
        0,
      );

      onCandidateCountChange(totalCandidates > 0);
    }
  }, [committeesWithNominations, onCandidateCountChange]);

  useEffect(() => {
    const currentYear = getNominationRoundYear();

    const candidateLookup = candidateArray
      ? candidateArray.reduce((acc, candidate) => {
          acc[candidate.id] = candidate.attributes.lastName || null;
          return acc;
        }, {})
      : {};

    const shortlistedAndElectedNominations = nominationArray
      .filter((item) => {
        const nominationReceivedEmailDate = proposerSeconder.find(
          (proposer) => proposer.attributes.nominationId === item.id,
        );

        const nominationReceivedDate =
          nominationReceivedEmailDate?.attributes?.nominationReceivedEmailDate;

        if (nominationReceivedDate) {
          return (
            currentStatus.includes(item.attributes.nominationStatusTypeId) &&
            getNominationRoundYear(new Date(nominationReceivedDate)) ===
              currentYear
          );
        }

        return null;
      })
      .map((nomination) => {
        nomination.attributes.candidate =
          candidateLookup[nomination.attributes.candidateId] || null;
        return nomination;
      });

    setNominations(shortlistedAndElectedNominations);
  }, [
    nominationArray,
    statusTypes,
    candidateArray,
    proposerSeconder,
    currentStatus,
  ]);

  useEffect(() => {
    let result = [];

    if (userCommittees.length > 0 && userCommittees[0]?.id === CM_PRIMARY_ID) {
      result = [
        {
          id: 14,
          attributes: {
            name: 'Corresponding Membership',
          },
        },
        {
          id: 15,
          attributes: {
            name: 'Special Election',
          },
        },
      ];
    } else if (isAdmin || isSCSecretary || isCouncilMember) {
      result = addCmseCommitteeToSectionalCommitteeArray(
        sectionalCommitteesArray,
        true,
      );
    } else if (isSCObserver && userCommittees.length > 0) {
      const observerCommitteeIds = userCommittees.map(
        (committee) => committee.id,
      );

      result = sectionalCommitteesArray.filter((committee) =>
        observerCommitteeIds.includes(committee.id),
      );
    } else if (userCommittees.length > 0) {
      // Regular member/chair with single committee
      const sectionalCommitteeId = userCommittees[0].id;
      result = [
        sectionalCommitteesArray.find(
          (item) => item.id === sectionalCommitteeId,
        ),
      ];
    }

    setSectionalCommitteeArray(result);
  }, [
    userCommittees,
    isAdmin,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    sectionalCommitteesArray,
  ]);

  return (
    <>
      {committeesWithNominations.map(
        (
          {
            committee,
            nominationsInSectionalCommittee,
            totalNominationCount,
            overlapNominations,
          },
          indexCategory,
        ) => {
          if (nominationsInSectionalCommittee.length > 0) {
            const getRowMargin = () => {
              if (indexCategory !== 0) return 'mt-20';
              return isCouncilBannerVisible ? 'mt-0' : 'mt-6';
            };
            const bannerActions =
              !isDisplayPDFDownloadButton && !isCouncilMemberOnly ? (
                <PDFDownloadButton
                  committeeId={committee.id}
                  nominationCount={totalNominationCount}
                />
              ) : null;
            return (
              <Row key={committee.id} className={getRowMargin()}>
                <Col>
                  <CommitteeBanner
                    name={committee.attributes.name}
                    actions={bannerActions}
                  />

                  <TableComponent
                    nominationsInSectionalCommittee={
                      nominationsInSectionalCommittee
                    }
                    overlapNominations={overlapNominations}
                    handleSelectClick={handleSelectClick}
                    handleStage2SelectClick={handleStage2SelectClick}
                    handleCOIClick={handleCOIClick}
                    onRankingSuccess={onRankingSuccess}
                    isSCChair={isSCChair}
                    shouldStartTour={indexCategory === 0}
                    allocatedNominationIds={allocatedNominationIds}
                    isCouncilMemberOnly={isCouncilMemberOnly}
                  />
                </Col>
              </Row>
            );
          }

          return null;
        },
      )}
    </>
  );
};

SectionalCommittees.propTypes = {
  TableComponent: PropTypes.elementType,
  statusTypes: PropTypes.array,
  handleSelectClick: PropTypes.func,
  handleStage2SelectClick: PropTypes.func,
  handleCOIClick: PropTypes.func,
  selectedCommittee: PropTypes.number,
  selectedNominationStatus: PropTypes.array,
  selectedRankingFilter: PropTypes.string,
  showMyAllocations: PropTypes.bool,
  onCandidateCountChange: PropTypes.func,
  onRankingSuccess: PropTypes.func,
  isSCChair: PropTypes.bool,
  enableCouncilMembers: PropTypes.bool,
  isCouncilBannerVisible: PropTypes.bool,
};

export default SectionalCommittees;
